/**
 * Board Import Service - Handle CSV/Excel import to board_items
 * Manages column mapping, duplicate detection, and batch inserts to Supabase
 */
import { supabase } from '../lib/supabase';
/**
 * Find existing items by title to detect duplicates
 */
async function findExistingItems(boardId, titles) {
    const { data, error } = await supabase
        .from('board_items')
        .select('id, title, data')
        .eq('board_id', boardId)
        .in('title', titles);
    if (error) {
        console.error('[BoardImport] Error fetching existing items:', error);
        return new Map();
    }
    const itemMap = new Map();
    if (data) {
        data.forEach((item) => {
            itemMap.set(item.title.toLowerCase(), item);
        });
    }
    return itemMap;
}
/**
 * Transform CSV row data based on column mapping
 */
function transformRowData(row, headers, columnMapping) {
    const data = {
        custom_fields: {},
    };
    headers.forEach((header) => {
        const boardField = columnMapping[header];
        const value = row[header];
        if (!boardField || value === null || value === '') {
            return; // Skip unmapped or empty fields
        }
        if (boardField === 'title') {
            data.title = String(value).trim();
        }
        else if (boardField === 'status') {
            data.data = data.data || {};
            data.data['status'] = String(value).trim();
        }
        else if (boardField === 'priority') {
            data.data = data.data || {};
            data.data['priority'] = String(value).trim();
        }
        else if (boardField === 'due_date') {
            data.data = data.data || {};
            try {
                const date = new Date(value);
                data.data['due_date'] = date.toISOString().split('T')[0]; // YYYY-MM-DD format
            }
            catch {
                console.warn(`Invalid date format for "${header}": ${value}`);
            }
        }
        else if (boardField === 'assignees') {
            // Parse assignees - could be comma-separated names or IDs
            const assigneeValue = String(value).trim();
            data.assignee_ids = assigneeValue.split(',').map((a) => a.trim()).filter(Boolean);
        }
        else if (boardField.startsWith('custom_')) {
            // Handle custom fields
            const fieldName = boardField.substring(7); // Remove 'custom_' prefix
            data.custom_fields[fieldName] = value;
        }
    });
    // Set required data object if not set
    if (!data.data) {
        data.data = {};
    }
    return data;
}
/**
 * Import items to board with duplicate handling
 */
export async function importItemsToBoard(config) {
    const result = {
        imported: 0,
        skipped: 0,
        updated: 0,
        failed: 0,
        errors: [],
    };
    try {
        // Get all existing titles for duplicate detection
        const titles = config.rows.map((row) => {
            const titleHeader = Object.keys(config.columnMapping).find((col) => config.columnMapping[col] === 'title');
            return titleHeader ? String(row[titleHeader]).trim() : '';
        }).filter(Boolean);
        const existingItems = await findExistingItems(config.boardId, titles);
        // Prepare items for insertion
        const itemsToInsert = [];
        const itemsToUpdate = [];
        for (const row of config.rows) {
            try {
                const transformedData = transformRowData(row, config.headers, config.columnMapping);
                if (!transformedData.title) {
                    result.skipped++;
                    result.errors.push('Row skipped: No title provided');
                    continue;
                }
                const existingItem = existingItems.get(transformedData.title.toLowerCase());
                if (existingItem) {
                    // Handle duplicate
                    if (config.duplicateAction === 'skip') {
                        result.skipped++;
                    }
                    else if (config.duplicateAction === 'overwrite') {
                        itemsToUpdate.push({
                            id: existingItem.id,
                            ...transformedData,
                        });
                        result.updated++;
                    }
                    else if (config.duplicateAction === 'add_new') {
                        // Add as new item - modify title to make it unique
                        transformedData.title = `${transformedData.title} (${Date.now()})`;
                        itemsToInsert.push({
                            board_id: config.boardId,
                            ...transformedData,
                        });
                        result.imported++;
                    }
                }
                else {
                    // New item
                    itemsToInsert.push({
                        board_id: config.boardId,
                        ...transformedData,
                    });
                    result.imported++;
                }
            }
            catch (error) {
                result.failed++;
                result.errors.push(`Row processing error: ${error instanceof Error ? error.message : 'Unknown error'}`);
            }
        }
        // Batch insert new items
        if (itemsToInsert.length > 0) {
            const { error: insertError } = await supabase
                .from('board_items')
                .insert(itemsToInsert);
            if (insertError) {
                result.errors.push(`Batch insert failed: ${insertError.message}`);
                result.failed += itemsToInsert.length;
                result.imported -= itemsToInsert.length;
                return result;
            }
        }
        // Batch update existing items
        if (itemsToUpdate.length > 0) {
            for (const item of itemsToUpdate) {
                const { error: updateError } = await supabase
                    .from('board_items')
                    .update({
                    title: item.title,
                    data: item.data,
                    custom_fields: item.custom_fields,
                    updated_at: new Date().toISOString(),
                })
                    .eq('id', item.id);
                if (updateError) {
                    result.errors.push(`Update failed for "${item.title}": ${updateError.message}`);
                    result.failed++;
                    result.updated--;
                }
            }
        }
        console.log('[BoardImport] Import completed:', result);
        return result;
    }
    catch (error) {
        const errorMsg = error instanceof Error ? error.message : 'Unknown error';
        result.errors.push(`Import failed: ${errorMsg}`);
        result.failed = config.rows.length;
        return result;
    }
}
/**
 * Detect duplicate items by title
 */
export async function detectDuplicates(boardId, rows, headers, columnMapping) {
    const titleHeader = Object.keys(columnMapping).find((col) => columnMapping[col] === 'title');
    if (!titleHeader) {
        return [];
    }
    const titles = rows.map((row) => String(row[titleHeader]).trim()).filter(Boolean);
    const existingItems = await findExistingItems(boardId, titles);
    const duplicates = [];
    rows.forEach((row) => {
        const title = String(row[titleHeader]).trim().toLowerCase();
        if (existingItems.has(title)) {
            duplicates.push({
                row,
                existingTitle: existingItems.get(title).title,
            });
        }
    });
    return duplicates;
}
/**
 * Validate column mapping
 */
export function validateColumnMapping(columnMapping) {
    const errors = [];
    // Check if title field is mapped
    const hasTitleMapping = Object.values(columnMapping).includes('title');
    if (!hasTitleMapping) {
        errors.push('Title field is required');
    }
    return {
        valid: errors.length === 0,
        errors,
    };
}
